import { angle, EPSILON, type Vec2, vec } from '../math';
import { type EntityId } from '../model/ids';

import { type Constraint } from './types';

/**
 * Residuals and analytic Jacobians.
 *
 * A constraint is satisfied when its residual is zero, so the solver's job is to drive
 * the residual vector to zero in a least-squares sense. Each term carries its own
 * gradient with respect to every point it touches.
 *
 * **Analytic, not numerical.** Central differences would be a third of the code and
 * would work — but each Jacobian would cost `2n` extra residual evaluations, inside a
 * loop that runs on every pointer move, and finite-difference error near tangency (where
 * the residual is nearly flat) is exactly where a geometry solver most needs precision.
 * Every derivative below is written out.
 *
 * ## Conditioning
 *
 * Angular constraints use **normalised** directions rather than raw cross and dot
 * products. `cross(u, v)` for two 1000-unit segments is a million times larger than for
 * two 1-unit ones, so an unnormalised parallel constraint would completely dominate a
 * distance constraint in the same cluster purely because of the scale of the drawing.
 * Normalising makes the residual an angle sine, which is scale-free.
 */

/** How a constraint's entity reference expands into points. */
export const SlotKind = {
  /** The entity is itself a point: one slot. */
  Point: 'point',
  /** A segment, ray, line or vector: its two defining points. */
  Linear: 'linear',
  /** A circle: its centre and the point that fixes its radius. */
  Circle: 'circle',
} as const;
export type SlotKind = (typeof SlotKind)[keyof typeof SlotKind];

export interface Slot {
  readonly entity: EntityId;
  readonly kind: SlotKind;
}

export function slotSize(kind: SlotKind): number {
  return kind === SlotKind.Point ? 1 : 2;
}

/**
 * The entity slots a constraint needs, in the order its residual expects the resulting
 * points.
 *
 * Keeping order in one place means the residual functions can index positionally without
 * every one of them re-deriving which point is which.
 */
export function slotsOf(constraint: Constraint): readonly Slot[] {
  switch (constraint.kind) {
    case 'fixed-distance':
      return [
        { entity: constraint.a, kind: SlotKind.Point },
        { entity: constraint.b, kind: SlotKind.Point },
      ];

    case 'equal-length':
    case 'parallel':
    case 'perpendicular':
      return [
        { entity: constraint.first, kind: SlotKind.Linear },
        { entity: constraint.second, kind: SlotKind.Linear },
      ];

    case 'horizontal':
    case 'vertical':
      return [{ entity: constraint.entity, kind: SlotKind.Linear }];

    case 'fixed-angle':
      return [
        { entity: constraint.a, kind: SlotKind.Point },
        { entity: constraint.vertex, kind: SlotKind.Point },
        { entity: constraint.c, kind: SlotKind.Point },
      ];

    case 'equal-angle':
      return [
        { entity: constraint.firstA, kind: SlotKind.Point },
        { entity: constraint.firstVertex, kind: SlotKind.Point },
        { entity: constraint.firstC, kind: SlotKind.Point },
        { entity: constraint.secondA, kind: SlotKind.Point },
        { entity: constraint.secondVertex, kind: SlotKind.Point },
        { entity: constraint.secondC, kind: SlotKind.Point },
      ];

    case 'concentric':
    case 'tangent':
      return [
        { entity: constraint.first, kind: SlotKind.Circle },
        { entity: constraint.second, kind: SlotKind.Circle },
      ];
  }
}

/** Total number of points a constraint's residual expects. */
export function pointArity(constraint: Constraint): number {
  let total = 0;
  for (const slot of slotsOf(constraint)) total += slotSize(slot.kind);
  return total;
}

/**
 * One scalar equation.
 *
 * `gradient[i]` is `∂value/∂points[i]`, positionally aligned with the point array handed
 * to `residualsOf`. Points the term does not depend on carry a zero gradient rather than
 * being omitted, so the assembler can copy without a lookup.
 */
export interface ResidualTerm {
  readonly value: number;
  readonly gradient: readonly Vec2[];
}

const ZERO = vec.ZERO;

function zeros(count: number): Vec2[] {
  return new Array<Vec2>(count).fill(ZERO);
}

/**
 * `∂(w/|w|)/∂w` applied to an upstream gradient.
 *
 * The Jacobian of normalisation is `(I − ŵ ŵᵀ)/|w|`; multiplying an upstream gradient
 * `g` by it gives `(g − ŵ (g·ŵ)) / |w|`. This is the projection of `g` onto the
 * direction perpendicular to `ŵ`, scaled by `1/|w|`, which is exactly right: changing a
 * vector's length cannot change its direction, so the gradient of any direction-only
 * quantity has no radial component.
 */
function throughNormalize(gradient: Vec2, unit: Vec2, length: number): Vec2 {
  if (length <= EPSILON) return ZERO;
  const radial = vec.dot(gradient, unit);
  return vec.scale(vec.subtract(gradient, vec.scale(unit, radial)), 1 / length);
}

/** `∂angle(w)/∂w = perp(w)/|w|²`. Zero for a degenerate vector. */
function angleGradient(w: Vec2): Vec2 {
  const lengthSquared = vec.lengthSquared(w);
  if (lengthSquared <= EPSILON * EPSILON) return ZERO;
  return vec.scale(vec.perpendicular(w), 1 / lengthSquared);
}

/**
 * Residuals for a constraint, given the current positions of its points.
 *
 * `points` must be `pointArity(constraint)` long, ordered as `slotsOf` describes.
 */
export function residualsOf(
  constraint: Constraint,
  points: readonly Vec2[]
): readonly ResidualTerm[] {
  const n = points.length;
  const at = (index: number): Vec2 => points[index] ?? ZERO;

  switch (constraint.kind) {
    case 'fixed-distance': {
      const a = at(0);
      const b = at(1);
      const delta = vec.subtract(a, b);
      const length = vec.length(delta);

      const gradient = zeros(n);
      if (length > EPSILON) {
        const unit = vec.scale(delta, 1 / length);
        gradient[0] = unit;
        gradient[1] = vec.negate(unit);
      }
      return [{ value: length - constraint.value, gradient }];
    }

    case 'equal-length': {
      const u = vec.subtract(at(1), at(0));
      const v = vec.subtract(at(3), at(2));
      const lengthU = vec.length(u);
      const lengthV = vec.length(v);

      const gradient = zeros(n);
      if (lengthU > EPSILON) {
        const unit = vec.scale(u, 1 / lengthU);
        gradient[0] = vec.negate(unit);
        gradient[1] = unit;
      }
      if (lengthV > EPSILON) {
        const unit = vec.scale(v, 1 / lengthV);
        gradient[2] = unit;
        gradient[3] = vec.negate(unit);
      }
      return [{ value: lengthU - lengthV, gradient }];
    }

    case 'parallel':
    case 'perpendicular': {
      const u = vec.subtract(at(1), at(0));
      const v = vec.subtract(at(3), at(2));
      const lengthU = vec.length(u);
      const lengthV = vec.length(v);

      const gradient = zeros(n);
      if (lengthU <= EPSILON || lengthV <= EPSILON) {
        return [{ value: 0, gradient }];
      }

      const unitU = vec.scale(u, 1 / lengthU);
      const unitV = vec.scale(v, 1 / lengthV);

      // Parallel: sin of the angle between them. Perpendicular: cosine.
      const isParallel = constraint.kind === 'parallel';
      const value = isParallel ? vec.cross(unitU, unitV) : vec.dot(unitU, unitV);

      // Gradients with respect to the *unit* vectors, before the normalisation chain.
      const dUnitU = isParallel ? vec.vec2(unitV.y, -unitV.x) : unitV;
      const dUnitV = isParallel ? vec.vec2(-unitU.y, unitU.x) : unitU;

      const dU = throughNormalize(dUnitU, unitU, lengthU);
      const dV = throughNormalize(dUnitV, unitV, lengthV);

      gradient[0] = vec.negate(dU);
      gradient[1] = dU;
      gradient[2] = vec.negate(dV);
      gradient[3] = dV;
      return [{ value, gradient }];
    }

    case 'horizontal': {
      // Linear in the coordinates, so no normalisation is needed for conditioning.
      const gradient = zeros(n);
      gradient[0] = vec.vec2(0, -1);
      gradient[1] = vec.vec2(0, 1);
      return [{ value: at(1).y - at(0).y, gradient }];
    }

    case 'vertical': {
      const gradient = zeros(n);
      gradient[0] = vec.vec2(-1, 0);
      gradient[1] = vec.vec2(1, 0);
      return [{ value: at(1).x - at(0).x, gradient }];
    }

    case 'fixed-angle': {
      const vertex = at(1);
      const u = vec.subtract(at(0), vertex);
      const v = vec.subtract(at(2), vertex);

      const gradient = zeros(n);
      if (vec.isZero(u) || vec.isZero(v)) return [{ value: 0, gradient }];

      const current = Math.atan2(vec.cross(u, v), vec.dot(u, v));
      // Wrapped to the shortest correction: driving 359° to 1° must be a 2° nudge, not
      // a 358° swing that overshoots and oscillates.
      const value = angle.normalizeSigned(current - constraint.value);

      const gradU = vec.negate(angleGradient(u));
      const gradV = angleGradient(v);

      gradient[0] = gradU;
      gradient[2] = gradV;
      gradient[1] = vec.negate(vec.add(gradU, gradV));
      return [{ value, gradient }];
    }

    case 'equal-angle': {
      const gradient = zeros(n);

      const firstVertex = at(1);
      const u1 = vec.subtract(at(0), firstVertex);
      const v1 = vec.subtract(at(2), firstVertex);

      const secondVertex = at(4);
      const u2 = vec.subtract(at(3), secondVertex);
      const v2 = vec.subtract(at(5), secondVertex);

      if (vec.isZero(u1) || vec.isZero(v1) || vec.isZero(u2) || vec.isZero(v2)) {
        return [{ value: 0, gradient }];
      }

      const first = Math.atan2(vec.cross(u1, v1), vec.dot(u1, v1));
      const second = Math.atan2(vec.cross(u2, v2), vec.dot(u2, v2));
      const value = angle.normalizeSigned(first - second);

      const gradU1 = vec.negate(angleGradient(u1));
      const gradV1 = angleGradient(v1);
      gradient[0] = gradU1;
      gradient[2] = gradV1;
      gradient[1] = vec.negate(vec.add(gradU1, gradV1));

      // The second angle enters with the opposite sign.
      const gradU2 = angleGradient(u2);
      const gradV2 = vec.negate(angleGradient(v2));
      gradient[3] = gradU2;
      gradient[5] = gradV2;
      gradient[4] = vec.negate(vec.add(gradU2, gradV2));

      return [{ value, gradient }];
    }

    case 'concentric': {
      // Two equations: the centres agree in both coordinates.
      const first = at(0);
      const second = at(2);

      const gradientX = zeros(n);
      gradientX[0] = vec.vec2(1, 0);
      gradientX[2] = vec.vec2(-1, 0);

      const gradientY = zeros(n);
      gradientY[0] = vec.vec2(0, 1);
      gradientY[2] = vec.vec2(0, -1);

      return [
        { value: first.x - second.x, gradient: gradientX },
        { value: first.y - second.y, gradient: gradientY },
      ];
    }

    case 'tangent': {
      const centreA = at(0);
      const rimA = at(1);
      const centreB = at(2);
      const rimB = at(3);

      const radiusVectorA = vec.subtract(rimA, centreA);
      const radiusVectorB = vec.subtract(rimB, centreB);
      const radiusA = vec.length(radiusVectorA);
      const radiusB = vec.length(radiusVectorB);

      const between = vec.subtract(centreA, centreB);
      const distance = vec.length(between);

      const gradient = zeros(n);
      if (distance <= EPSILON || radiusA <= EPSILON || radiusB <= EPSILON) {
        return [{ value: 0, gradient }];
      }

      const unitBetween = vec.scale(between, 1 / distance);
      const unitA = vec.scale(radiusVectorA, 1 / radiusA);
      const unitB = vec.scale(radiusVectorB, 1 / radiusB);

      // External: D = rA + rB. Internal: D = |rA − rB|, whose sign flips with which
      // circle is larger — the branch has to be read from the current configuration.
      const external = constraint.tangentKind === 'external';
      const sign = external ? 1 : radiusA >= radiusB ? 1 : -1;
      const target = external ? radiusA + radiusB : sign * (radiusA - radiusB);
      const value = distance - target;

      // ∂D/∂centreA = unitBetween;  ∂rA/∂centreA = −unitA;  ∂rA/∂rimA = unitA
      const radiusSignA = external ? 1 : sign;
      const radiusSignB = external ? 1 : -sign;

      gradient[0] = vec.add(unitBetween, vec.scale(unitA, radiusSignA));
      gradient[1] = vec.scale(unitA, -radiusSignA);
      gradient[2] = vec.add(vec.negate(unitBetween), vec.scale(unitB, radiusSignB));
      gradient[3] = vec.scale(unitB, -radiusSignB);

      return [{ value, gradient }];
    }
  }
}

/** Number of scalar equations a constraint contributes. */
export function residualCount(constraint: Constraint): number {
  return constraint.kind === 'concentric' ? 2 : 1;
}
